import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import LocationCard from '../components/LocationCard';
import SmartRecommendations from '../components/SmartRecommendations';
import EmergencyQuickActions from '../components/EmergencyQuickActions';
import CategoryGrid from '../components/CategoryGrid';
import QuickActions from '../components/QuickActions';
import TravelTips from '../components/TravelTips';
import ResultsList from '../components/ResultsList';
import { reverseGeocode, fetchNearbyPlaces } from '../services/geoService';
import { calculateDistance } from '../utils/geoUtils';
import { trackClick, trackEvent } from '../utils/analytics';

const HomeView = ({ location, setLocation, address, setAddress, city, setCity, countryCode, setCountryCode, bookmarks, setBookmarks, setHistory, setSelectedPlace }) => {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [activeCategory, setActiveCategory] = useState('');
  const [error, setError] = useState(null);

  const handleDetect = () => {
    trackClick('Detect Location', 'Home');
    setLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const newLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLocation(newLoc);
        trackEvent('location_detected', 'Geofence', `${newLoc.lat},${newLoc.lng}`);
        try {
          const details = await reverseGeocode(newLoc.lat, newLoc.lng);
          if (details) {
            setAddress(details.fullAddress);
            setCity(details.city);
            setCountryCode(details.countryCode);
            setHistory(prev => [{
              id: Date.now().toString(),
              address: details.fullAddress,
              lat: newLoc.lat,
              lng: newLoc.lng,
              city: details.city,
              countryCode: details.countryCode,
              timestamp: new Date().toISOString()
            }, ...prev].slice(0, 50));
          }
        } catch (e) {
          console.warn('Geocoding failed');
        } finally {
          setLoading(false);
        }
      },
      () => {
        setError({ type: 'system', message: 'Location access denied.' });
        setLoading(false);
      }
    );
  };

  const handleSearch = async (category) => {
    trackClick(`Search: ${category}`, 'Discovery');
    if (!location) {
      setError({ type: 'system', message: 'Please detect location first.' });
      return;
    }
    setSearching(true);
    setError(null);
    setActiveCategory(category);
    try {
      const data = await fetchNearbyPlaces(location.lat, location.lng, category);
      const processed = data.elements.map(el => {
        const tags = el.tags || {};
        // Enhanced Classification
        const classification = tags.cuisine || tags.operator || tags.amenity || tags.shop || 'Local Spot';
        const description = tags.description || tags.note || (tags.cuisine ? `Serving ${tags.cuisine} specialties.` : '');

        return {
          id: el.id.toString(),
          name: tags.name || 'Local Spot',
          lat: el.lat || el.center?.lat,
          lng: el.lon || el.center?.lon,
          distance: calculateDistance(location.lat, location.lng, el.lat || el.center?.lat, el.lon || el.center?.lon),
          tagType: classification.replace(/_/g, ' '),
          description: description,
          tags: tags
        };
      })
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 30); // Cap at 30 results as requested

      setResults(processed);
      setTimeout(() => document.getElementById('results-view')?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      setError({ type: 'system', message: 'Map servers busy.' });
    } finally {
      setSearching(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <LocationCard location={location} onDetect={handleDetect} loading={loading} address={address} city={city} countryCode={countryCode} />
      {location && (
        <>
          <SmartRecommendations onSelectCategory={handleSearch} />
          <EmergencyQuickActions onSearch={handleSearch} disabled={searching} />
          <CategoryGrid onSearch={handleSearch} disabled={searching} />
          <QuickActions onSearch={handleSearch} disabled={searching} />
          <TravelTips countryCode={countryCode} city={city} />
          <div id="results-view" className="scroll-mt-6 min-h-[100px]">
            {searching && (
              <div className="flex flex-col items-center py-16 bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200 mb-8">
                <div className="w-10 h-10 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin"></div>
                <p className="mt-4 font-black text-[9px] uppercase tracking-[0.3em] text-slate-800">Scanning Area...</p>
              </div>
            )}
            <ResultsList 
              results={results} 
              categoryLabel={activeCategory} 
              bookmarks={bookmarks} 
              onToggleBookmark={(item) => setBookmarks(prev => prev.find(b => b.id === item.id) ? prev.filter(b => b.id !== item.id) : [...prev, item])} 
              onSelectPlace={setSelectedPlace} 
            />
          </div>
        </>
      )}
    </motion.div>
  );
};

export default HomeView;